import { PagePaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { Box, CircularProgress, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo } from "react";
import {
  type CommercialMonitoringDetailEntity,
  type CommercialMonitoringDetailRow,
  type GetCommercialMonitoringDetailsAPIResponse,
} from "@/features/commercialRegistrationMonitoring/types";
import { getDisplayReceptionReason } from "@/features/commercialRegistrationMonitoring/utils/getDisplayReceptionReason";
import { GetCommercialMonitoringDetailAPI } from "../api";
import { useApiClient } from "@/hooks/useApiClient";
import { CommercialMonitoringDetailDataGrid } from "../components/CommercialMonitoringDetailDataGrid";
import { KeyboardArrowLeft } from "@mui/icons-material";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import { HttpStatusCode, isAxiosError } from "axios";
import { toast } from "react-toastify";
import { format } from "date-fns";

/**
 * 商業登記モニタリング詳細画面（監視対象＝法人一覧）
 */
export const CommercialMonitoringDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiClient } = useApiClient();

  const initialize = (
    details: CommercialMonitoringDetailEntity[]
  ): CommercialMonitoringDetailRow[] => {
    return details.map((detail) => ({
      id: detail.id,
      commercialRegistrationNumber:
        detail.requesterCommercialRegistrationNumber,
      corporateName: detail.corporateName,
      registrationDate: detail.registrationDate,
      remarks: detail.remarks,
    }));
  };

  const { data, isLoading, error } = GetCommercialMonitoringDetailAPI(
    apiClient,
    Number(id)
  );

  const count = data?.count ?? 0; // 件数
  const receptionReason = data
    ? getDisplayReceptionReason(data.summary.receptionReasons)
    : ""; // 登記原因
  const monthlyUpdatableLabel =
    data == null || data.summary.monthlyUpdatable == null
      ? ""
      : data.summary.monthlyUpdatable
      ? "する"
      : "しない";
  const legalAffairsBureauRequestDateStart = data?.summary
    .legalAffairsBureauRequestDateStart
    ? String(data.summary.legalAffairsBureauRequestDateStart)
    : "";
  const legalAffairsBureauRequestDateEnd = data?.summary
    .legalAffairsBureauRequestDateEnd
    ? String(data.summary.legalAffairsBureauRequestDateEnd)
    : "";

  // モニタリング詳細結果
  const rows = useMemo(() => (data ? initialize(data.details) : []), [data]);

  // 「商業登記取得」ボタン押下時の処理
  const handleClickAcquireCommercialBook = (
    row: CommercialMonitoringDetailRow
  ): void => {
    const params = new URLSearchParams({
      companyName: row.corporateName,
      corporateNumber: row.commercialRegistrationNumber,
    });
    navigate(`/commercialbook?${params.toString()}`);
  };

  useEffect(() => {
    if (
      isAxiosError<GetCommercialMonitoringDetailsAPIResponse>(error) &&
      (error.response?.status === HttpStatusCode.BadRequest ||
        error.response?.status === HttpStatusCode.NotFound)
    ) {
      toast.error("指定のデータが存在しません。");
      return;
    }
    if (
      isAxiosError<GetCommercialMonitoringDetailsAPIResponse>(error) &&
      (error.response?.status ?? HttpStatusCode.InternalServerError) >=
        HttpStatusCode.InternalServerError
    ) {
      toast.error(
        "データの取得に失敗しました。しばらく経ってから再度お試しください。"
      );
    }
  }, [error]);

  return (
    <PagePaper>
      {/* ページタイトル */}
      <Box sx={{ display: "flex", mb: 3 }}>
        <MonitorHeartIcon sx={{ mr: 1 }} fontSize="large" />
        <PageTitle>商業登記モニタリング詳細</PageTitle>
      </Box>

      {/* 件数と登記原因 */}
      <Stack direction={"column"} spacing={1} sx={{ mt: 3 }}>
        <Stack direction={"row"} spacing={1}>
          <Typography>件数：</Typography>
          <Typography sx={{ color: "#DF9B4A", fontWeight: "bold" }}>
            {count}
          </Typography>
          <Typography>件</Typography>
        </Stack>
        <Stack direction={"row"} spacing={1}>
          <Typography>登記原因：</Typography>
          <Typography sx={{ color: "#DF9B4A", fontWeight: "bold" }}>
            {receptionReason}
          </Typography>
        </Stack>
        <Stack direction={"row"} spacing={1}>
          <Typography>月次更新：</Typography>
          <Typography sx={{ color: "#DF9B4A", fontWeight: "bold" }}>
            {monthlyUpdatableLabel}
            {monthlyUpdatableLabel === "しない" &&
            legalAffairsBureauRequestDateStart !== "" &&
            legalAffairsBureauRequestDateEnd !== ""
              ? `（${format(
                  new Date(legalAffairsBureauRequestDateStart),
                  "yyyy/MM/dd"
                )} 〜 ${format(
                  new Date(legalAffairsBureauRequestDateEnd),
                  "yyyy/MM/dd"
                )}）`
              : ""}
          </Typography>
        </Stack>
      </Stack>

      {/* 結果 */}
      <Box sx={{ mt: 3 }}>
        {isLoading || data == null ? (
          <Stack alignItems="center">
            <CircularProgress sx={{ my: "20px" }} />
          </Stack>
        ) : (
          <CommercialMonitoringDetailDataGrid
            navigate={navigate}
            rows={rows}
            handleClickAcquireCommercialBook={handleClickAcquireCommercialBook}
          />
        )}
      </Box>

      {/* 戻るリンク */}
      <Box sx={{ mt: 3 }}>
        <Link
          component={RouterLink}
          to={"/commercial-monitoring"}
          sx={{ display: "inline-block" }}
        >
          <Box sx={{ display: "flex" }}>
            <KeyboardArrowLeft />
            <Typography>商業登記モニタリング一覧に戻る</Typography>
          </Box>
        </Link>
      </Box>
    </PagePaper>
  );
};
